import { defineStore } from 'pinia'
import { getCurrentLocale } from '@/i18n'


export const MENU_KEY = "web-menu";
export const PERSON_MENU_KEY = "web-menu-person";
export const PERMISSIONS_KEY = "web-permissions";
// 个人区(person layout)权限单独存储: 项目权限在切换项目时整体替换, 不再与旧值串接累积
// (旧实现 concat 会让 A 项目的权限码在 B 项目里残留, 形成越权面)
export const PERSON_PERMISSIONS_KEY = "web-permissions-person";
// Locale the cached menu was fetched under. The menu labels are localised by
// the backend (sys_menu.name_en_hk / name_zh_hk), so a cache fetched under one
// locale must be re-fetched when the UI locale changes — see layout/index.vue.
export const MENU_LOCALE_KEY = "web-menu-locale";

interface MENU {
  menu: any[]
  personMenu: any[]
  permissions: any[]
}

const readJson = (key: string): any[] => {
  const raw = window.localStorage.getItem(key)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (_e) {
    return []
  }
}

// state.permissions = 当前项目权限 ∪ 个人区权限 (去重)
const unionPermissions = (): any[] => {
  return Array.from(new Set([...readJson(PERMISSIONS_KEY), ...readJson(PERSON_PERMISSIONS_KEY)]))
}

// 项目菜单里第一个可见叶子页面的 href。用户角色可能不含 首页(/home) —
// 例如仅有 门诊运营 的 qabot 项目账号 — 进入项目后应落在其菜单的第一个
// 页面而非硬编码 /home。有 首页 的用户第一个可见叶子就是 /home，行为不变。
export const firstMenuHref = (menus: any[]): string | null => {
  for (const item of menus || []) {
    if (!item?.showFlag) continue
    if (item.type === '2' && item.href) return item.href
    if (item.type === '1') {
      for (const child of item.children || []) {
        if (child?.showFlag && child.type === '2' && child.href) return child.href
      }
    }
  }
  return null
}

export const useMenuStore = defineStore('menu', {
  state: (): MENU => ({
    menu: [],
    personMenu: [],
    permissions: []
  }),
  actions: {
    hasPermission(codes: string | string[]): boolean {
      const list = Array.isArray(codes) ? codes : [codes]
      return list.some(code => this.permissions.includes(code))
    },
    getMenu() {
      const menu = window.localStorage.getItem(MENU_KEY)
      if (menu) {
        try {
          this.menu = JSON.parse(menu) as any[];
        } catch (_e) { }
      }
      this.permissions = unionPermissions();
    },
    getPersonMenu() {
      const menu: any = window.localStorage.getItem(PERSON_MENU_KEY)
      if (menu) {
        try {
          this.personMenu = JSON.parse(menu) as any[];
        } catch (_e) { }
      }
      this.permissions = unionPermissions();
    },
    setPersonMenu(menu: any, permissions: any) {
      if (menu) {
        window.localStorage.setItem(PERSON_MENU_KEY, JSON.stringify(menu))
      }
      if (permissions) {
        window.localStorage.setItem(PERSON_PERMISSIONS_KEY, JSON.stringify(permissions))
      }
      this.permissions = unionPermissions();
    },
    setMenu(menus: any, permissions: any) {
      if (menus) {
        window.localStorage.setItem(MENU_KEY, JSON.stringify(menus))
        window.localStorage.setItem(MENU_LOCALE_KEY, getCurrentLocale())
      }
      if (permissions) {
        // 整体替换当前项目权限
        window.localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions))
      }
      this.permissions = unionPermissions();
    },
    // Replace the cached menu in-place (reactive state + localStorage) after a
    // locale-driven re-fetch. Unlike setMenu it leaves permissions untouched —
    // permissions don't change with locale.
    refreshMenu(menus: any) {
      if (menus) {
        window.localStorage.setItem(MENU_KEY, JSON.stringify(menus))
        window.localStorage.setItem(MENU_LOCALE_KEY, getCurrentLocale())
        this.menu = menus as any[]
      }
    }
  }
})
